/**
 * Reception's status card: the queues against their targets, and each desk's pill.
 * All of it read off the live branch state the page already holds.
 */
import { callingWaitSeconds } from './callingWait'
import { deskQueueIds } from './floorBoard'
import { deskAvailability } from './receptionDesk'
import type { CounterView, QueueView, Ticket, WorkProfile } from './types'

/** Where a queue stands against its target. Close is the last quarter of the target time. */
export type SlaState = 'idle' | 'ok' | 'close' | 'over'
export const CLOSE_AT = 0.75

/** The phrase that says n as a place: 1st, 2nd, 3rd, 11th, 22nd. */
export function ordinalKey(n: number): 'reception.ord.1' | 'reception.ord.2' | 'reception.ord.3' | 'reception.ord.n' {
  const tens = n % 100
  if (tens >= 11 && tens <= 13) return 'reception.ord.n'
  const ones = n % 10
  return ones === 1 ? 'reception.ord.1' : ones === 2 ? 'reception.ord.2' : ones === 3 ? 'reception.ord.3' : 'reception.ord.n'
}

/** A wait against its target: the share used, and the state that share means. */
export function slaOf(waitSec: number, targetSec: number | undefined): { ratio: number | null; state: Exclude<SlaState, 'idle'> } {
  if (!targetSec) return { ratio: null, state: 'ok' }
  const ratio = waitSec / targetSec
  return { ratio, state: ratio >= 1 ? 'over' : ratio >= CLOSE_AT ? 'close' : 'ok' }
}

export interface QueueRow {
  queue: QueueView
  waiting: number
  longestSec: number
  /** The longest wait over its own target, 0..1+; null when no waiting ticket has a target. */
  ratio: number | null
  sla: SlaState
  openDesks: number
}

/**
 * One row per queue. Each ticket is judged against its own target, as the server's
 * breaching is; the queue's state is its worst ticket's.
 */
export function queueRows(
  queues: readonly QueueView[],
  tickets: readonly Ticket[],
  counters: readonly Pick<CounterView, 'state' | 'status' | 'ticketId' | 'workProfileId' | 'queueIds'>[],
  profiles: readonly Pick<WorkProfile, 'id' | 'queueIds'>[] | null,
  now: number,
): QueueRow[] {
  const open = counters.filter((c) => deskAvailability(c) !== 'closed')
  return queues.map((queue) => {
    const waitingHere = tickets.filter((t) => t.state === 'waiting' && t.queueId === queue.queueId)
    let longestSec = 0
    let ratio: number | null = null
    for (const t of waitingHere) {
      const waited = callingWaitSeconds(t, now)
      longestSec = Math.max(longestSec, waited)
      const r = slaOf(waited, t.serviceLevelSec).ratio
      if (r !== null) ratio = Math.max(ratio ?? 0, r)
    }
    const sla: SlaState = waitingHere.length === 0 ? 'idle' : slaOf(ratio ?? 0, ratio === null ? undefined : 1).state
    return {
      queue,
      waiting: waitingHere.length,
      longestSec,
      ratio,
      sla,
      openDesks: open.filter((c) => deskQueueIds(c, profiles ?? []).includes(queue.queueId)).length,
    }
  })
}

export type DeskPill = { kind: 'serving' | 'calling' | 'break' | 'free' | 'closed'; number?: string }

/** A desk's status pill: who it is serving or calling, else its availability. */
export function deskPill(desk: Pick<CounterView, 'state' | 'status' | 'ticketId' | 'ticketState' | 'displayNumber'>): DeskPill {
  const a = deskAvailability(desk)
  if (a === 'serving' && desk.displayNumber) return { kind: desk.ticketState === 'called' ? 'calling' : 'serving', number: desk.displayNumber }
  return { kind: a }
}

/**
 * When the desk's current customer entered the state its pill names: the call for
 * Calling, the start of the work for Serving (as the Counter page's clock). None
 * for a free, paused or closed desk.
 */
export function deskSince(
  desk: Pick<CounterView, 'state' | 'status' | 'ticketId'>,
  tickets: readonly Pick<Ticket, 'id' | 'state' | 'lastCalledAt' | 'transactions' | 'currentTransaction'>[],
): string | undefined {
  if (deskAvailability(desk) !== 'serving' || !desk.ticketId) return undefined
  const t = tickets.find((x) => x.id === desk.ticketId)
  if (!t) return undefined
  if (t.state === 'called') return t.lastCalledAt
  return t.transactions[t.currentTransaction]?.startedAt ?? t.lastCalledAt
}
